import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Receipt } from "lucide-react";
import { useGetPurchases } from "@/hooks/api/query/useGetPurchases";
import type { Purchase } from "@/api/types/paymentGateway";

function formatDate(dateStr: string): string {
  return new Date(dateStr).toLocaleDateString("ro-RO", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function formatAmount(amount: number, currency: string): string {
  return new Intl.NumberFormat("ro-RO", {
    style: "currency",
    currency: currency.toUpperCase(),
  }).format(amount / 100);
}

function LoadingSkeleton() {
  return (
    <div className="space-y-3">
      {Array.from({ length: 3 }).map((_, i) => (
        <Skeleton key={i} className="h-12 w-full" />
      ))}
    </div>
  );
}

function EmptyState() {
  return (
    <div className="text-center py-8 text-muted-foreground">
      <Receipt className="h-10 w-10 mx-auto mb-3 opacity-50" />
      <p>Nu ai efectuat nicio achiziție încă.</p>
    </div>
  );
}

function PurchaseRow({ purchase }: { purchase: Purchase }) {
  return (
    <TableRow>
      <TableCell className="text-sm text-muted-foreground whitespace-nowrap font-mono">
        {formatDate(purchase.createdAt)}
      </TableCell>
      <TableCell className="text-sm">
        {purchase.credits !== null ? `${purchase.credits} credite` : "—"}
      </TableCell>
      <TableCell className="text-right text-sm whitespace-nowrap">
        {formatAmount(purchase.amount, purchase.currency)}
      </TableCell>
    </TableRow>
  );
}

export default function PurchaseHistory() {
  const { data, isLoading } = useGetPurchases();
  const purchases = data?.purchases ?? [];

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center gap-2">
          <Receipt className="h-5 w-5" />
          <CardTitle>Istoric plăți</CardTitle>
        </div>
        <CardDescription>Achizițiile tale anterioare</CardDescription>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <LoadingSkeleton />
        ) : purchases.length === 0 ? (
          <EmptyState />
        ) : (
          <div className="rounded-md border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Data</TableHead>
                  <TableHead>Credite</TableHead>
                  <TableHead className="text-right">Sumă</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {purchases.map((purchase) => (
                  <PurchaseRow key={purchase.id} purchase={purchase} />
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
